import { useQueryClient } from "@tanstack/react-query";
import { ModalShell } from "../ModalShell";
import { ModalLoadingSkeleton } from "./ModalLoadingSkeleton";
import { ModalErrorPanel } from "./ModalErrorPanel";
import { useSubstanceInventory } from "@/hooks/useSubstanceInventory";
import { useSubstanceFileUrl } from "@/hooks/useSubstanceFileUrl";
import { parseSubstanceKey } from "@/utils/types/substance";
import { lookupServerCompound } from "@/utils/serverCompounds";
import type { InventoryFileSlot } from "@/utils/types/substance";

type InventoryModalProps = {
  compoundId: string;
  onClose: () => void;
};

export function InventoryModal({ compoundId, onClose }: InventoryModalProps) {
  const qc  = useQueryClient();
  const sub = parseSubstanceKey(compoundId);
  const inv = useSubstanceInventory(sub?.kind ?? null, sub?.id ?? null);
  const { mutateAsync: getFileUrl } = useSubstanceFileUrl();
  const sc  = lookupServerCompound(compoundId);

  const handleFile = async (inventoryId: string, slot: InventoryFileSlot) => {
    if (!sub) return;
    const { url } = await getFileUrl({ kind: sub.kind, id: sub.id, inventoryId, slot });
    window.open(url, "_blank", "noopener");
  };

  const shellHeader = {
    open: true as const,
    onClose,
    eyebrow: "Inventory",
    title: sc?.name ?? "—",
    accentVar: "var(--brand-green)",
  };

  if (inv.isPending) {
    return (
      <ModalShell {...shellHeader} subtitle="">
        <ModalLoadingSkeleton kind="inventory" />
      </ModalShell>
    );
  }
  if (inv.isError) {
    return (
      <ModalShell {...shellHeader} subtitle="">
        <ModalErrorPanel
          onRetry={() => qc.invalidateQueries({ queryKey: ["substanceInventory"] })}
        />
      </ModalShell>
    );
  }

  const rows = inv.data?.rows ?? [];
  const totalQty = rows.reduce((s, r) => s + r.quantity, 0);
  const inStock = rows.filter((r) => r.outOfStockAt === null).length;
  const subtitle = `${sc?.cas ?? "—"} · ${rows.length} lot${
    rows.length === 1 ? "" : "s"
  } · ${inStock} in stock · ${totalQty.toFixed(0)} total units`;

  return (
    <ModalShell {...shellHeader} subtitle={subtitle}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Catalog no.</th>
            <th>Supplier</th>
            <th>Item name</th>
            <th>Size</th>
            <th>Lot</th>
            <th style={{ textAlign: "right" }}>Qty</th>
            <th style={{ textAlign: "right" }}>Price</th>
            <th>Files</th>
            <th>Received</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={r.outOfStockAt ? { opacity: 0.55 } : undefined}>
              <td className="mono">{r.catalogNo}</td>
              <td>{r.supplier.name}</td>
              <td>{r.label}</td>
              <td className="mono">{r.sizeLabel}</td>
              <td className="mono" style={{ color: "var(--ink-4)" }}>
                {r.lotNo ?? "—"}
              </td>
              <td className="mono" style={{ textAlign: "right" }}>
                {r.quantity}
              </td>
              <td className="mono" style={{ textAlign: "right" }}>
                {r.unitPriceLabel}
              </td>
              <td>
                {r.files.coa && (
                  <button
                    type="button"
                    className="btn xs ghost"
                    onClick={() => handleFile(r.id, "coa")}
                  >
                    CoA
                  </button>
                )}{" "}
                {r.files.sds && (
                  <button
                    type="button"
                    className="btn xs ghost"
                    onClick={() => handleFile(r.id, "sds")}
                  >
                    SDS
                  </button>
                )}{" "}
                {r.files.tds && (
                  <button
                    type="button"
                    className="btn xs ghost"
                    onClick={() => handleFile(r.id, "tds")}
                  >
                    TDS
                  </button>
                )}
                {!r.files.coa && !r.files.sds && !r.files.tds && (
                  <span style={{ color: "var(--ink-4)" }}>—</span>
                )}
              </td>
              <td className="mono" style={{ color: "var(--ink-3)" }}>
                {r.receivedAt ?? "—"}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={9}
                style={{
                  padding: 28,
                  textAlign: "center",
                  color: "var(--ink-4)",
                }}
              >
                No inventory on hand for this substance.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </ModalShell>
  );
}
